import type { ProposalState } from "@/domains/application/constant/status";
import type { JobApplicationStatus } from "@/domains/company/company/api/types";

/**
 * Quali candidature entrano nel report.
 *
 * `statuses` e `proposalStates` sono tassonomie **alternative**, scelte da
 * `initiator`: le candidature spontanee vivono su `status`, le proposte inviate
 * dall'azienda su `proposal_state`. Mandarle insieme è un 400, non un AND.
 *
 * `contactedOnly` invece è **ortogonale**: si somma agli stati selezionati
 * invece di sostituirli. È la distinzione che i pill dell'ATS confondono, e che
 * il dialog rende esplicita.
 */
export type ReportFilters = {
  initiator: "freelancer" | "company";
  statuses: JobApplicationStatus[];
  proposalStates: ProposalState[];
  contactedOnly: boolean;
};

export const DEFAULT_REPORT_FILTERS: ReportFilters = {
  initiator: "freelancer",
  statuses: [],
  proposalStates: [],
  contactedOnly: false,
};

/** Forma di rete (snake_case). Le chiavi vuote restano fuori dal body. */
export type ReportFiltersPayload = {
  initiator?: "freelancer" | "company";
  statuses?: JobApplicationStatus[];
  proposal_states?: ProposalState[];
  contacted_only?: boolean;
};

/**
 * Forma canonica, gemella di `normalizeReportFilters` lato backend.
 *
 * Serve a due cose: non mandare rumore che lo schema rifiuta (la tassonomia
 * dell'initiator non scelto), e far sì che due selezioni equivalenti producano
 * la stessa query key — altrimenti si interroga due volte lo stesso conteggio.
 */
export const toFiltersPayload = (
  filters: ReportFilters,
): ReportFiltersPayload | undefined => {
  const payload: ReportFiltersPayload = {};

  if (filters.initiator === "company") {
    if (filters.proposalStates.length > 0) {
      payload.initiator = "company";
      payload.proposal_states = [...filters.proposalStates].sort();
    } else {
      payload.initiator = "company";
    }
  } else if (filters.statuses.length > 0) {
    // Gli stati descrivono le candidature spontanee; senza l'initiator il
    // backend pescherebbe anche le proposte aziendali (status "pending").
    payload.initiator = "freelancer";
    payload.statuses = [...filters.statuses].sort();
  }

  if (filters.contactedOnly) {
    payload.contacted_only = true;
  }

  return Object.keys(payload).length > 0 ? payload : undefined;
};

/** Vero quando il report copre tutti i candidati dell'offerta. */
export const isUnfiltered = (filters: ReportFilters): boolean =>
  toFiltersPayload(filters) === undefined;
